import type { UserDto } from "@piano/shared";
import { ApiError, configureApi } from "../api/client.js";
import { api } from "../api/index.js";

type Status = "loading" | "anonymous" | "ready";

class Session {
  status = $state<Status>("loading");
  user = $state<UserDto | null>(null);
  /** Gesetzt, wenn die Sitzung von außen beendet wurde (z. B. Passwortwechsel) */
  expiredNotice = $state(false);

  constructor() {
    configureApi({
      csrfToken: null,
      onUnauthorized: () => this.expire(),
      refreshCsrf: async () => {
        try {
          const session = await api.session();
          configureApi({ csrfToken: session.csrfToken });
          return true;
        } catch {
          return false;
        }
      },
    });
  }

  async load(): Promise<void> {
    try {
      const session = await api.session();
      configureApi({ csrfToken: session.csrfToken });
      this.user = session.user;
      this.status = "ready";
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) throw error;
      this.status = "anonymous";
    }
  }

  async login(email: string, password: string): Promise<void> {
    const session = await api.login(email, password);
    configureApi({ csrfToken: session.csrfToken });
    this.user = session.user;
    this.expiredNotice = false;
    this.status = "ready";
  }

  async logout(): Promise<void> {
    try {
      await api.logout();
    } finally {
      this.clear();
    }
  }

  /** Sitzung ungültig (401 oder vom Server beendet). */
  expire(): void {
    if (this.status !== "ready") return;
    this.expiredNotice = true;
    this.clear();
  }

  private clear(): void {
    configureApi({ csrfToken: null });
    this.user = null;
    this.status = "anonymous";
  }

  get isAdmin(): boolean {
    return this.user?.role === "admin";
  }
}

export const session = new Session();
